import { defineStore } from 'pinia';
import { ConnectionType } from '@sembo-private/maps';
import { computed, ref, watch } from 'vue';

import { i18n } from '@apps/main/modules/localization/localizationSetup';
import {
  MapStateKey,
  RecipeStateKey,
  TripStateKey,
} from '@apps/main/modules/state/state';
import {
  useBrowserStore,
  useRouterStore,
  useStatsigEventsStore,
  useUserLocationStore,
} from '@apps/main/new-desktop/core/stores';
import { useEditingIngredientStore } from '@apps/main/modules/store/editingIngredientStore';
import type {
  HotelIngredient,
  TransportIngredient,
} from '@apps/main/modules/recipe/ingredients';
import {
  FlightIngredient,
  isFerryIngredient,
  isFlightIngredient,
  FerryIngredient,
} from '@apps/main/modules/recipe/ingredients';
import { AutoCompleteType } from '@apps/main/new-desktop/modules/autoComplete';
import type { Port } from '@apps/main/modules/recipe/ingredients/ferryIngredient';

import { TransportType } from '@libs/trip-planner-interfaces';
import { injectStrict } from '@libs/utils';

import {
  usePortSelectorStore,
  useTripOverviewModalsStore,
  useTripOverviewPageStore,
} from '../../stores';

import type {
  CarStartPosition,
  FerryTripSettings,
  FlightTripSettings,
} from './TripTypeSelectorPanel.model';
import { TripType } from './TripTypeSelectorPanel.model';

const { t } = i18n.global;

export const useTripTypeSelectorStore = defineStore(
  'TripTypeSelectorStore',
  () => {
    const recipeState = injectStrict(RecipeStateKey);
    const tripState = injectStrict(TripStateKey);
    const mapState = injectStrict(MapStateKey);

    const browserStore = useBrowserStore();
    const routerStore = useRouterStore();
    const statsigEventsStore = useStatsigEventsStore();

    const userLocationStore = useUserLocationStore();
    const tripOverviewModalsStore = useTripOverviewModalsStore();
    const editingIngredientStore = useEditingIngredientStore();
    const tripOverviewPageStore = useTripOverviewPageStore();
    const portSelectorStore = usePortSelectorStore();

    // State
    const hotelTripSettings = ref<HotelIngredient | undefined>(undefined);
    const carTripSettings = ref<CarStartPosition | undefined>(undefined);
    const flightTripSettings = ref<FlightTripSettings | undefined>(undefined);
    const ferryTripSettings = ref<FerryTripSettings | undefined>(undefined);

    watch(
      mapState.startingLocation,
      () => {
        if (hotelTripSettings.value) {
          setTripTypeOptions(hotelTripSettings.value);
        }
      },
      { immediate: true }
    );

    // Actions
    async function confirmTripTypeSelectorPanel(
      selectedTripType: TripType
    ): Promise<void> {
      statsigEventsStore.logTripTypeSelectorSelectedTripType(selectedTripType);

      switch (selectedTripType) {
        case TripType.Car:
          if (
            userLocationStore.startLocation.name !== carTripSettings.value?.name
          ) {
            userLocationStore.setUserStartLocation(
              carTripSettings.value?.placeId
            );
          }
          if (recipeState.recipe.value[0]) {
            recipeState.recipe.value[0].transportTypeToBeReached =
              ConnectionType.CAR;
            recipeState.setReturnLocation();
          }
          if (browserStore.isLargerThanSmallMax) {
            await routerStore.navigateToCarTrip();
          }
          tripState.doneEditingTripType();

          break;

        case TripType.Flight:
          if (!flightTripSettings.value) {
            return;
          }

          if (flightTripSettings.value.outbound) {
            recipeState.addIngredient(flightTripSettings.value.outbound, 0);
          }
          if (flightTripSettings.value.homebound) {
            recipeState.addIngredient(flightTripSettings.value.homebound);
          }
          tripState.doneEditingTripType();

          break;

        case TripType.Ferry:
          if (!ferryTripSettings.value) {
            return;
          }
          if (ferryTripSettings.value.outbound) {
            recipeState.addIngredient(ferryTripSettings.value.outbound, 0);
          }
          if (ferryTripSettings.value.homebound) {
            recipeState.addIngredient(ferryTripSettings.value.homebound);
          }
          tripState.doneEditingTripType();

          break;

        case TripType.Hotel:
          tripOverviewPageStore.setUserSelectedTransport(true);
          tripState.doneEditingTripType();

          break;
      }
    }

    function editTripTypeIngredient(selectedTripType: TripType): void {
      switch (selectedTripType) {
        case TripType.Flight:
          if (!recipeState.recipe.value[0]) {
            return;
          }
          recipeState.createTransportIngredient(ConnectionType.FLIGHT);
          recipeState.editIngredientTransport(recipeState.recipe.value[0]);
          tripOverviewModalsStore.openPortSelectionModal(
            editingIngredientStore.editingTransportIngredient?.ingredientId,
            tripOverviewPageStore.getFromPortInfoFromEditingIngredient(),
            tripOverviewPageStore.getToPortInfoFromEditingIngredient(),
            TransportType.Flight,
            false
          );
          break;
        case TripType.Ferry:
          if (!recipeState.recipe.value[0]) {
            return;
          }
          recipeState.createTransportIngredient(ConnectionType.FERRY);
          recipeState.editIngredientTransport(recipeState.recipe.value[0]);
          tripOverviewModalsStore.openPortSelectionModal(
            editingIngredientStore.editingTransportIngredient?.ingredientId,
            tripOverviewPageStore.getFromPortInfoFromEditingIngredient(),
            tripOverviewPageStore.getToPortInfoFromEditingIngredient(),
            TransportType.Ferry,
            false
          );
          break;
        case TripType.Car:
          tripOverviewModalsStore.openDestinationSelectorModal(
            '',
            undefined,
            AutoCompleteType.StartingPosition
          );
          break;
        case TripType.Hotel:
        default:
          break;
      }
    }

    function selectTripType(selectedTripType: TripType): void {
      switch (selectedTripType) {
        case TripType.Flight:
          if (!flightTripSettings.value) {
            return;
          }
          if (!flightTripSettings.value.outbound) {
            editTripTypeIngredient(TripType.Flight);
          }
          break;
        case TripType.Ferry:
          if (!ferryTripSettings.value) {
            return;
          }
          if (!ferryTripSettings.value.outbound) {
            editTripTypeIngredient(TripType.Ferry);
          }
          break;
        case TripType.Car:
        case TripType.Hotel:
        default:
          break;
      }
    }

    function setTripTypeOptions(newHotelIngredient: HotelIngredient): void {
      hotelTripSettings.value = newHotelIngredient;
      portSelectorStore
        .tryToGetSuggestedAirports(newHotelIngredient.displayText)
        .subscribe((airport) => {
          if (airport.departureAirport && airport.arrivalAirport) {
            const outboundFlight = new FlightIngredient();
            outboundFlight.setDepartureAirport(airport.departureAirport);
            outboundFlight.setArrivalAirport(airport.arrivalAirport);
            const homebound = recipeState.getMirroredTransport(
              outboundFlight
            ) as FlightIngredient;
            flightTripSettings.value = {
              homebound: homebound,
              outbound: outboundFlight,
            };
          }
        });

      portSelectorStore
        .tryToGetSuggestedOutboundFerryRoutePortNames(
          newHotelIngredient.displayText
        )
        .subscribe((ferryRoute) => {
          if (ferryRoute[0]) {
            const outbound = new FerryIngredient();
            const fromPort: Port = {
              iata: ferryRoute[0].data.from.code,
              displayingText: ferryRoute[0].data.from.label,
            };
            const toPort: Port = {
              iata: ferryRoute[0].data.to.code,
              displayingText: ferryRoute[0].data.to.label,
            };
            outbound.setPorts(fromPort, toPort);

            const homebound = recipeState.getMirroredTransport(
              outbound
            ) as FerryIngredient;

            ferryTripSettings.value = { homebound, outbound };
          }
        });
      setCarTripSettings();
    }

    function setCarTripSettings(): void {
      const carStartPosition: CarStartPosition = {
        name:
          userLocationStore.startLocation.name === t('planner.your_position')
            ? userLocationStore.startLocation.name
            : `${userLocationStore.startLocation.name}, ${
                userLocationStore.startLocation.country ?? ''
              }`,
      };
      carTripSettings.value = carStartPosition;
    }

    function setTripTypeTransportIngredient(
      transportIngredient: TransportIngredient,
      transportType: 'outbound' | 'homebound'
    ): void {
      if (isFlightIngredient(transportIngredient)) {
        if (transportType === 'outbound') {
          flightTripSettings.value = {
            outbound: transportIngredient,
            homebound: flightTripSettings.value?.homebound,
          };
        }
        if (transportType === 'homebound') {
          flightTripSettings.value = {
            homebound: transportIngredient,
            outbound: flightTripSettings.value?.outbound,
          };
        }
      }

      if (isFerryIngredient(transportIngredient)) {
        if (transportType === 'outbound') {
          ferryTripSettings.value = {
            outbound: transportIngredient,
            homebound: ferryTripSettings.value?.homebound,
          };
        }
        if (transportType === 'homebound') {
          ferryTripSettings.value = {
            homebound: transportIngredient,
            outbound: ferryTripSettings.value?.outbound,
          };
        }
      }
    }

    function setTripTypeStartPosition(newValue: CarStartPosition): void {
      carTripSettings.value = newValue;
    }

    const destinationName = computed<string>(() =>
      hotelTripSettings.value
        ? hotelTripSettings.value.displayText.split(',')[0] ?? ''
        : ''
    );

    const departureAirport = computed<string>(() =>
      flightTripSettings.value?.outbound
        ? flightTripSettings.value.outbound.fromPortDisplayText ?? ''
        : ''
    );

    const departurePort = computed<string>(() =>
      ferryTripSettings.value?.outbound
        ? `${ferryTripSettings.value.outbound.fromPortDisplayText} - ${ferryTripSettings.value.outbound.toPortDisplayText}`
        : ''
    );

    const carStartPosition = computed<string>(
      () => carTripSettings.value?.name ?? ''
    );

    return {
      // State
      hotelTripSettings,
      carTripSettings,
      flightTripSettings,
      ferryTripSettings,

      // Actions
      confirmTripTypeSelectorPanel,
      editTripTypeIngredient,
      selectTripType,
      setTripTypeOptions,
      setTripTypeTransportIngredient,
      setTripTypeStartPosition,

      // Getters
      destinationName,
      departureAirport,
      departurePort,
      carStartPosition,
    };
  }
);
